import { useCallback, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { getImageUrl, userMasterApi } from "../../api/masterApi";
import { getAdminUser, isProviderRole, isVerifierRole } from "../../utils/auth";
import {
  calculateProfileCompletion,
  PROFILE_UPDATED_EVENT,
} from "../../utils/profileCompletion";

export default function AdminHeader() {
  const user = getAdminUser();
  const showProgress = isProviderRole() || isVerifierRole();
  const [completion, setCompletion] = useState(0);
  const [profilePicture, setProfilePicture] = useState("");

  const applyProfile = useCallback((profile) => {
    if (!profile) return;
    setCompletion(calculateProfileCompletion(profile));
    const nextPicture = profile.profile_picture || "";
    if (nextPicture && nextPicture !== "selected" && nextPicture !== "pending-upload") {
      setProfilePicture(nextPicture);
    } else if (!nextPicture) {
      setProfilePicture("");
    }
  }, []);

  const loadProviderProfile = useCallback(async () => {
    if (!showProgress || !user?.reference_id) {
      setCompletion(0);
      setProfilePicture("");
      return;
    }
    try {
      const profile = await userMasterApi.getOne(user.reference_id);
      applyProfile(profile);
    } catch {
      setCompletion(0);
      setProfilePicture("");
    }
  }, [applyProfile, showProgress, user?.reference_id]);

  useEffect(() => {
    loadProviderProfile();
  }, [loadProviderProfile]);

  useEffect(() => {
    if (!showProgress) return undefined;

    const onProfileUpdated = (event) => {
      const detail = event.detail || {};
      if (detail.profile) {
        applyProfile(detail.profile);
        return;
      }
      if (typeof detail.completion === "number") {
        setCompletion(detail.completion);
      }
      if (detail.profile_picture !== undefined) {
        setProfilePicture(detail.profile_picture || "");
      }
      if (detail.profile_picture === undefined && detail.completion === undefined) {
        loadProviderProfile();
      }
    };

    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, [applyProfile, loadProviderProfile, showProgress]);

  const avatarUrl = getImageUrl(profilePicture);

  return (
    <header className="admin-header">
      <div className="admin-header__search">
        <input type="text" placeholder="Search here..." />
        <button type="button" aria-label="Search">
          <SearchIcon fontSize="small" />
        </button>
      </div>

      {showProgress && (
        <div
          className="admin-header__progress"
          role="progressbar"
          aria-valuenow={completion}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Profile completion"
          title={`Profile completion ${completion}%`}
        >
          <div
            className="admin-header__progress-fill"
            style={{ width: `${completion}%` }}
          />
          <span className="admin-header__progress-label">{completion}%</span>
        </div>
      )}

      <div className="admin-header__actions">
        <div className="admin-header__profile">
          <div className="admin-header__avatar">
            {showProgress && avatarUrl ? (
              <img src={avatarUrl} alt={user?.name || "Profile"} />
            ) : (
              <PersonOutlineIcon fontSize="small" />
            )}
          </div>
          <div className="admin-header__profile-text">
            <p>Hello {user?.name || "Admin"}</p>
            <span>{user?.role || "Admin"}</span>
          </div>
        </div>
      </div>
    </header>
  );
}
